// src/hooks/useAuthenticatedDelete.js

import { useState, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import axios from 'axios';
import { getAuthScopes } from '../config/scopes';
import { InteractionRequiredAuthError } from '@azure/msal-browser';

/**
 * Custom hook to handle authenticated DELETE requests.
 * Utilizes dynamic scopes based on environment configurations.
 */
const useAuthenticatedDelete = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { instance, accounts } = useMsal();

  /**
   * Performs an authenticated DELETE request to the specified URL.
   *
   * @param {string} url - The API endpoint to send the DELETE request to.
   * @returns {Promise<Object>} - The response data from the API.
   */
  const deleteRequest = useCallback(
    async (url) => {
      if (!accounts.length) {
        const noAccountError = new Error('No authenticated account found');
        setError(noAccountError);
        throw noAccountError;
      }

      setIsLoading(true);
      setError(null);

      try {
        const scopes = getAuthScopes();

        if (scopes.length === 0) {
          throw new Error('No authentication scopes defined in REACT_APP_AUTH_SCOPES.');
        }

        const accessTokenResponse = await instance.acquireTokenSilent({
          scopes: scopes,
          account: accounts[0],
        });

        const config = {
          headers: {
            Authorization: `Bearer ${accessTokenResponse.accessToken}`,
          }
        };

        console.log('DELETE request config:', config);
        console.log('DELETE request URL:', url);

        // Make the authenticated DELETE request
        const response = await axios.delete(url, config);

        return response.data;
      } catch (err) {
        console.error('Error deleting data:', err);
        console.error('Error details:', {
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data,
          headers: err.response?.headers
        });
        setError(err);

        if (err instanceof InteractionRequiredAuthError) {
          const scopes = getAuthScopes();

          if (scopes.length === 0) {
            console.error('No authentication scopes defined in REACT_APP_AUTH_SCOPES.');
            return;
          }

          instance.acquireTokenRedirect({
            scopes: scopes,
          });
        }

        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [instance, accounts]
  );

  const resetError = useCallback(() => {
    setError(null);
  }, []);

  // Export 'delete' to match the destructuring in your component
  return { delete: deleteRequest, isLoading, error, resetError };
};

export default useAuthenticatedDelete;